import { Box, Chip, Grid, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OtSidePanel from "./OtSidePanel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import dayjs from "dayjs";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { dataGridStyle, colors } from "../theme";
import { useState, useEffect, useMemo } from "react";
import API from "../api";
import IntelligenceTimeFilter from "./IntelligenceTimeFilter";
import OtToolBar from "./OtToolBar";
import { startToast, updateToastDismiss, updateToastError, updateToastWarning } from "../utils/notification-loading";

const OtDataGrid = ({
  reseller = false,
  tipo,
  passedPayload,
  onRowsLoaded = null,
  onTimeFilterChange = null,
  onFilterModelChange = null,
  mode = null,
  localRows = null,
  columnVisibilityOverride = {},
  anomalyMode = null,
  // Vista "OT History" divisa per classe: filtra per `class` sul backend e usa
  // un set di colonne dedicato passato da fuori. Retro-compatibili (null di default).
  otClass = null,
  columnsOverride = null,
  // Optional controlled drawer state — lets a sibling component (e.g. the OT Monitor chart)
  // open the same drawer for a row that isn't a grid selection. Uncontrolled (internal state)
  // when omitted, so every other page using this grid keeps working unchanged.
  panelRow: controlledPanelRow,
  onPanelRowChange = null,
  stats = null,
  configRange = null,
  // When true, this grid renders no time filter of its own and instead tracks passedPayload's
  // dateFrom/dateTo reactively — used by OT Monitor, which renders the time filter inline in its
  // chart header instead. Every other page keeps rendering its own IntelligenceTimeFilter as before.
  hideTimeFilter = false,
}) => {

  const [rows, setRows] = useState([]);
  const [charged, setCharged] = useState(true);
  const [internalPanelRow, setInternalPanelRow] = useState(null);
  const otPanelRow = controlledPanelRow !== undefined ? controlledPanelRow : internalPanelRow;
  const setOtPanelRow = onPanelRowChange ?? setInternalPanelRow;
  const [rowCounter, setRowCounter] = useState(0);
  const [filterModel, setFilterModel] = useState(() => {
    if (passedPayload.ipMap === true) {
      return [
        { field: "source_h", operator: "equals", value: passedPayload.source },
        { logic: "or", field: "destination_h", operator: "equals", value: passedPayload.destination },
      ];
    }
    const transformedFilter = [
      ...(passedPayload.ipSource
        ? [{ field: "source_h", operator: "contains", value: passedPayload.ipSource }]
        : []),
      ...(passedPayload.ipDestination
        ? [{ logic: "and", field: "destination_h", operator: "contains", value: passedPayload.ipDestination }]
        : []),
    ];
    return transformedFilter;
  });

  const [source] = useState("");
  const [destination] = useState("");
  const [protocol] = useState(null);
  const [operation] = useState(null);
  const [isWrite] = useState(passedPayload.isWrite ?? null);
  const [status] = useState(null);
  const [subnet] = useState("");
  const [hideExternalWrites] = useState(false);

  const [internalTimeFilter, setInternalTimeFilter] = useState({
    dateFrom: passedPayload.dateFrom,
    dateTo: passedPayload.dateTo
  });
  // Reactively tracks passedPayload when hideTimeFilter — memoized so it's only a new reference
  // when the dates actually change, not on every render (this feeds a fetch-triggering effect).
  const controlledTimeFilter = useMemo(
    () => ({ dateFrom: passedPayload.dateFrom, dateTo: passedPayload.dateTo }),
    [passedPayload.dateFrom, passedPayload.dateTo],
  );
  const timeFilter = hideTimeFilter ? controlledTimeFilter : internalTimeFilter;
  const setTimeFilter = setInternalTimeFilter;
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  // Propagate time filter changes to parent (for chart sync)
  useEffect(() => {
    if (!timeFilter.dateFrom || !timeFilter.dateTo) return;
    onTimeFilterChange?.([
      timeFilter.dateFrom.utc().toISOString(),
      timeFilter.dateTo.utc().toISOString(),
    ]);
  }, [timeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Propagate filter model changes to parent (for chart sync)
  useEffect(() => {
    onFilterModelChange?.(filterModel);
  }, [filterModel]); // eslint-disable-line react-hooks/exhaustive-deps

  //API
  useEffect(() => {
    const abortCtrl = new AbortController();
    const signal = abortCtrl.signal;

    const buildPayload = () => {
      // Inject is_write as first filter entry when mode is set, so it's never lost on filter changes
      const modeFilter =
        mode === "w"
          ? [{ field: "is_write", operator: "equals", value: true }]
          : mode === "r"
          ? [{ field: "is_write", operator: "equals", value: false }]
          : [];

      let combinedFilter;
      if (modeFilter.length > 0 && filterModel.length > 0) {
        // Ensure first user filter has logic: "and" since modeFilter occupies position 0
        const firstUser = filterModel[0].logic
          ? filterModel[0]
          : { logic: "and", ...filterModel[0] };
        combinedFilter = [modeFilter[0], firstUser, ...filterModel.slice(1)];
      } else {
        combinedFilter = [...modeFilter, ...filterModel];
      }

      return {
        timeFilter: [
          timeFilter.dateFrom.utc().toISOString(),
          timeFilter.dateTo.utc().toISOString(),
        ],
        pageNumber: paginationModel.page,
        limit: paginationModel.pageSize,
        ...(protocol && { protocol }),
        ...(operation && { operation }),
        ...(status && { status }),
        ...(source && { source }),
        ...(destination && { destination }),
        ...(subnet && { subnet }),
        ...(hideExternalWrites && { hideExternalWrites }),
        ...(otClass && { class: otClass }),
        ...(combinedFilter.length > 0 && { filter: combinedFilter }),
      };
    };

    setCharged(true);
    startToast();

    API.getOtLog(buildPayload(), signal)
      .then((res) => {
        const newData = res.data.data.map((item, index) => ({
          id: `${paginationModel.page}-${index}`,
          // Espone tutti i campi del documento (envelope + specifici di classe +
          // src_mac/dest_mac/object/command/target/stream/topic/file/url/document + extra),
          // così le colonne per-classe possono leggerli senza whitelist.
          ...item,
          // Normalizzazioni retro-compatibili con le colonne esistenti:
          value: item.value ?? item.value_dec,
          customNameDst: item.customNameDst ?? item.customNameDest,
        }));

        setRows(newData);
        setRowCounter(res.data.totalLogs);
        onRowsLoaded?.(newData);
        updateToastDismiss();
      })
      .catch((err) => {
        if (err?.name === "AbortError") {
          updateToastDismiss();
          return;
        }

        const httpStatus = err?.response?.status;
        if (httpStatus === 408) {
          updateToastWarning("Timeout");
        } else {
          updateToastError("Errore");
        }
      })
      .finally(() => setCharged(false));

    return () => abortCtrl.abort();
  }, [
    timeFilter,
    paginationModel,
    protocol,
    operation,
    isWrite,
    status,
    source,
    destination,
    subnet,
    hideExternalWrites,
    filterModel,
    otClass,
    mode,
    onRowsLoaded,
  ]);


  return (
    <Box my={"20px"}>
      <Grid container spacing={2}>
        {tipo !== -2 && !hideTimeFilter &&
          <Grid size={12}>
            <IntelligenceTimeFilter
              initialDateFrom={timeFilter.dateFrom}
              initialDateTo={timeFilter.dateTo}
              onDateChange={setTimeFilter}
            />
          </Grid>
        }
        <Grid size={12} sx={{ height: "80vh" }}>
          <Box height={"100%"} sx={{ width: "111.11%", height: "111.11%", transform: "scale(0.9)", transformOrigin: "top left" }}>
            <DataGridPro
              sx={dataGridStyle}
              rows={localRows !== null ? localRows : rows}
              columns={[
                {
                  field: "__info",
                  headerName: "",
                  width: 50,
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  hideable: false,
                  align: "center",
                  headerAlign: "center",
                  renderCell: (params) => (
                    <Tooltip title="View event details">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setOtPanelRow(params.row); }}
                        sx={{ color: colors.grey[100], "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" } }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  ),
                },
                ...(columnsOverride || otColumns(mode, anomalyMode)),
              ]}
              disableColumnFilter
              columnVisibilityModel={
                columnsOverride
                  ? { ...columnVisibilityOverride }
                  : {
                      is_write: mode === "r" || mode === "w" ? false : true,
                      transport: false,
                      ts_resp: false,
                      quantity: false,
                      ...columnVisibilityOverride,
                    }
              }
              getRowId={(row) => row.id}
              pagination
              paginationMode={localRows !== null ? "client" : "server"}
              rowCount={localRows !== null ? localRows.length : rowCounter}
              pageSizeOptions={[30, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              showToolbar
              loading={charged}
              getRowHeight={() => "auto"}
              slots={{
                toolbar: OtToolBar,
              }}
              slotProps={{
                loadingOverlay: { variant: "skeleton" },
                toolbar: {
                  filterModel,
                  setFilterModel,
                  mode,
                  printOptions: { disableToolbarButton: true },
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* ── OT event details side panel ───────────────────────────────── */}
      <OtSidePanel
        row={otPanelRow}
        open={Boolean(otPanelRow)}
        onClose={() => setOtPanelRow(null)}
        anomalyMode={anomalyMode}
        stats={stats}
        configRange={configRange}
      />
    </Box>
  );
}

// Endpoint cell with hostname-first display + IP:port on hover (same UX as
// the security grid). Picks the first available name (dns → netbios → custom),
// falls back to the raw IP. Hover swaps the text to ip:port and underlines.
const HostnameCell = ({ ip, port, hostnames, legacy }) => {
  const name =
    hostnames?.dns_name ||
    hostnames?.netbios_name ||
    hostnames?.custom_name ||
    legacy?.dns_name ||
    legacy?.netbios_name ||
    legacy?.custom_name ||
    "";
  const display = `${name || ip || "-"}${port != null && port !== "" ? `:${port}` : ""}`;
  const hover   = `${ip || "-"}${port != null && port !== "" ? `:${port}` : ""}`;
  const tooltipTitle = name ? `${ip}:${port ?? ""}` : "";
  return (
    <Tooltip title={tooltipTitle} arrow disableHoverListener={!name}>
      <span
        style={{ cursor: "default" }}
        onMouseEnter={(e) => {
          if (!name) return;
          e.currentTarget.innerText = hover;
          e.currentTarget.style.textDecoration = "underline";
        }}
        onMouseLeave={(e) => {
          if (!name) return;
          e.currentTarget.innerText = display;
          e.currentTarget.style.textDecoration = "none";
        }}
      >
        {display}
      </span>
    </Tooltip>
  );
};

export const otColumns = (mode = null, anomalyMode = null) => {
  const anomalyHeader =
    anomalyMode === "outOfRange"
      ? "OUT OF RANGE"
      : anomalyMode === "externalIp"
      ? "EXTERNAL IP"
      : "ANOMALY";

  const anomalyLabels =
    anomalyMode === "outOfRange"
      ? { true: "Value out of range", false: "Value within range" }
      : anomalyMode === "externalIp"
      ? { true: "External IP detected", false: "All IPs internal" }
      : { true: "Anomaly detected", false: "Normal" };

  return [
  // Anomaly — always visible, useful for all modes
  {
    field: "anomaly",
    headerName: anomalyHeader,
    minWidth: 60,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      const val = anomalyMode === "outOfRange" ? params.row.outOfRange : params.value;
      if (val === true)
        return (
          <Tooltip title={anomalyLabels.true}>
            <WarningAmberIcon sx={{ color: "#ffb422" }} />
          </Tooltip>
        );
      if (val === false)
        return (
          <Tooltip title={anomalyLabels.false}>
            <CheckCircleOutlineIcon sx={{ color: "#4254fb" }} />
          </Tooltip>
        );
      return (
        <Tooltip title="N/D">
          <HelpOutlineIcon sx={{ color: "#777" }} />
        </Tooltip>
      );
    },
  },
  {
    field: "timestamp",
    headerName: "TIMESTAMP",
    minWidth: 160,
    flex: 1,
  },
  {
    field: "src_ip",
    headerName: "SOURCE",
    minWidth: 180,
    renderCell: (params) => (
      <HostnameCell
        ip={params.value}
        port={params.row.src_port}
        hostnames={params.row.src_hostnames}
        legacy={{
          dns_name:     params.row.dnsNameSrc,
          netbios_name: params.row.netBiosNameSrc,
          custom_name:  params.row.customNameSrc || params.row.hostNameSrc,
        }}
      />
    ),
  },
  {
    field: "dest_ip",
    headerName: "DESTINATION",
    minWidth: 180,
    renderCell: (params) => (
      <HostnameCell
        ip={params.value}
        port={params.row.dest_port}
        hostnames={params.row.dest_hostnames}
        legacy={{
          dns_name:     params.row.dnsNameDst,
          netbios_name: params.row.netBiosNameDst,
          custom_name:  params.row.customNameDst || params.row.hostNameDest,
        }}
      />
    ),
  },
  {
    field: "protocol",
    headerName: "PROTOCOL",
    minWidth: 100,
  },
  {
    field: "operation",
    headerName: "OPERATION",
    minWidth: 180,
    flex: 2,
  },
  {
    field: "address",
    headerName: "ADDRESS",
    minWidth: 130,
    flex: 1,
  },
  {
    field: "data_type",
    headerName: "TYPE",
    minWidth: 100,
  },
  {
    field: "value",
    headerName: "VALUE",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "status",
    headerName: "STATUS",
    minWidth: 80,
  },
  // R/W — hidden in dedicated r/w pages; shown as chip in monitor mode
  {
    field: "is_write",
    headerName: "R/W",
    minWidth: 70,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      if (params.value === true)
        return <Chip label="W" size="small" sx={{ bgcolor: "#4254fb22", color: "#4254fb", fontSize: 10, height: 20 }} />;
      if (params.value === false)
        return <Chip label="R" size="small" sx={{ bgcolor: "#4cceac22", color: "#4cceac", fontSize: 10, height: 20 }} />;
      return null;
    },
  },
  // Lower-priority columns — hidden by default, restorable via column menu
  {
    field: "transport",
    headerName: "TRANSPORT",
    minWidth: 80,
  },
  {
    field: "quantity",
    headerName: "QUANTITY",
    minWidth: 70,
  },
  {
    field: "ts_resp",
    headerName: "RESPONSE TIMESTAMP",
    minWidth: 160,
    renderCell: (params) => {
      if (!params.value) return "";
      const d = dayjs(params.value);
      return d.isValid() ? d.format("YYYY-MM-DD HH:mm:ss") : "";
    },
  },
  ];
};
// ── Vista "OT History" divisa per classe ─────────────────────────────────────
// Ordine delle tab; "unknown" mostrato come "Altro".
export const OT_CLASSES = [
  { key: "memory", label: "Memory" },
  { key: "object", label: "Object" },
  { key: "command", label: "Command" },
  { key: "session", label: "Session" },
  { key: "telemetry", label: "Telemetry" },
  { key: "messaging", label: "Messaging" },
  { key: "discovery", label: "Discovery" },
  { key: "document", label: "Document" },
  { key: "file_transfer", label: "File transfer" },
  { key: "media_control", label: "Media control" },
  { key: "unknown", label: "Altro" },
];

// Campo identificatore per la ricerca rapida di ogni classe (usato dalla toolbar).
export const OT_CLASS_ID_FIELD = {
  memory: "address",
  object: "object",
  command: "command",
  session: "target",
  telemetry: "stream",
  messaging: "topic",
  discovery: "target",
  document: "document",
  file_transfer: "file",
  media_control: "url",
  unknown: "operation",
};

// Cella "—" per campi assenti (i campi vuoti non vengono salvati sui documenti).
const dashCell = (params) => {
  const v = params.value;
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const rwCell = (params) => {
  if (params.value === true)
    return <Chip label="W" size="small" sx={{ bgcolor: "#4254fb22", color: "#4254fb", fontSize: 10, height: 20 }} />;
  if (params.value === false)
    return <Chip label="R" size="small" sx={{ bgcolor: "#4cceac22", color: "#4cceac", fontSize: 10, height: 20 }} />;
  return "—";
};

// Source/Destination: ip:port (con hostname come il resto del grid), oppure
// src_mac/dest_mac per eventi link-layer (transport="ethernet", niente ip/port).
const endpointColumn = (side) => ({
  field: side === "src" ? "src_ip" : "dest_ip",
  headerName: side === "src" ? "SOURCE" : "DESTINATION",
  minWidth: 180,
  renderCell: (params) => {
    const row = params.row;
    if (row.transport === "ethernet") {
      const mac = side === "src" ? row.src_mac : row.dest_mac;
      return mac || "—";
    }
    return (
      <HostnameCell
        ip={params.value}
        port={side === "src" ? row.src_port : row.dest_port}
        hostnames={side === "src" ? row.src_hostnames : row.dest_hostnames}
        legacy={side === "src"
          ? { dns_name: row.dnsNameSrc, netbios_name: row.netBiosNameSrc, custom_name: row.customNameSrc || row.hostNameSrc }
          : { dns_name: row.dnsNameDst, netbios_name: row.netBiosNameDst, custom_name: row.customNameDst || row.hostNameDest }}
      />
    );
  },
});

const textCol = (field, headerName, extra = {}) => ({ field, headerName, minWidth: 120, renderCell: dashCell, ...extra });

// Colonne comuni a tutte le classi (envelope).
const otCommonColumns = () => [
  { field: "timestamp", headerName: "TIMESTAMP", minWidth: 160, flex: 1, renderCell: dashCell },
  { field: "protocol", headerName: "PROTOCOL", minWidth: 100, renderCell: dashCell },
  { field: "operation", headerName: "OPERATION", minWidth: 160, flex: 1, renderCell: dashCell },
  endpointColumn("src"),
  endpointColumn("dst"),
  { field: "transport", headerName: "TRANSPORT", minWidth: 90, renderCell: dashCell },
  { field: "status", headerName: "STATUS", minWidth: 90, renderCell: dashCell },
];

// Colonne specifiche per classe (oltre all'envelope; assenti se vuote → "—").
const OT_CLASS_SPECIFIC = {
  memory: [
    textCol("area", "AREA"),
    textCol("address", "ADDRESS", { flex: 1 }),
    { field: "is_write", headerName: "R/W", minWidth: 70, align: "center", headerAlign: "center", renderCell: rwCell },
    textCol("quantity", "QUANTITY", { minWidth: 90 }),
    textCol("data_type", "TYPE", { minWidth: 90 }),
    textCol("value", "VALUE", { flex: 1 }),
    textCol("value_hex", "VALUE (HEX)", { flex: 1 }),
  ],
  object: [
    textCol("object", "OBJECT", { flex: 1 }),
    { field: "is_write", headerName: "R/W", minWidth: 70, align: "center", headerAlign: "center", renderCell: rwCell },
    textCol("data_type", "TYPE", { minWidth: 90 }),
    textCol("value", "VALUE", { flex: 1 }),
    textCol("value_hex", "VALUE (HEX)", { flex: 1 }),
  ],
  command: [
    textCol("command", "COMMAND", { flex: 1 }),
    { field: "is_write", headerName: "R/W", minWidth: 70, align: "center", headerAlign: "center", renderCell: rwCell },
    textCol("value", "VALUE", { flex: 1 }),
  ],
  session: [textCol("target", "TARGET", { flex: 1 })],
  telemetry: [textCol("stream", "STREAM", { flex: 1 })],
  messaging: [textCol("topic", "TOPIC", { flex: 1 })],
  discovery: [textCol("target", "TARGET", { flex: 1 })],
  document: [textCol("document", "DOCUMENT", { flex: 1 })],
  file_transfer: [textCol("file", "FILE", { flex: 1 })],
  media_control: [textCol("url", "URL", { flex: 1 })],
  unknown: [],
};

// Colonne complete per una classe: comuni + specifiche. `extra` resta nel side panel.
export const otClassColumns = (classKey) => [
  ...otCommonColumns(),
  ...(OT_CLASS_SPECIFIC[classKey] || []),
];

export default OtDataGrid;
